import { AutoCartSetupItem, AutoCartSetupItemDraft } from './autoCartSetup.types';
import type { ScheduleBatch } from './scheduleBatch';
import { createStore } from 'cross-state';
import equals from 'fast-deep-equal/es6';

export type ScheduleSnapshot = {
    setups?: AutoCartSetupItem[];
    ready: boolean;
    scopeStoreGuid?: string;
    initialSnapshot?: number;
    resync: () => void;
};
export type ScheduleView = { setups?: AutoCartSetupItem[]; isLoading: boolean; error: boolean; initialSnapshot?: number };
export const scheduleScopeKey = (storeGuid?: string) => JSON.stringify([storeGuid ?? null]);
// Conflict checks normalize legacy whitespace; received values and confirmations stay exact.
export function sameScheduleFrame(left?: string | null, right?: string | null) {
    const frame = left?.trim();
    return !!frame && frame === right?.trim();
}
export type ScheduleObservation = { setups: AutoCartSetupItem[]; scopeStoreGuid?: string; initialSnapshot: number };
type CommandResult = 'accepted' | 'blocked' | 'inactive' | 'unavailable' | 'interrupted';
type CommandIntent =
    | { kind: 'immediate'; request: AutoCartSetupItemDraft; scopeStoreGuid?: string }
    | { kind: 'update'; request: AutoCartSetupItemDraft; baseline?: AutoCartSetupItem }
    | { kind: 'delete'; guid: string; storeGuid: string; baseline?: AutoCartSetupItem };
export type ScheduleCommand = { intent: CommandIntent; batch?: ScheduleBatch } & (
    | { phase: 'submitting'; seen: Set<string> }
    | { phase: 'pending'; expected?: AutoCartSetupItem; recoveryAfter?: number }
    | { phase: 'failed'; error: unknown }
);
function isRejectedResponse(error: unknown, endpoint: 'single' | 'bulk') {
    const status = (error as { response?: { status?: number } })?.response?.status;
    // Single-item handlers also return 400 for database errors with unknown outcomes.
    return (endpoint === 'bulk' ? [400, 401, 403, 409, 413] : [401, 403, 413]).includes(status ?? 0);
}
const fields = ['frameNumber', 'scheduledCartId', 'validFrom', 'validTo', 'storeGuid', 'cartSetupServiceParameters'] as const;
export const immediateKey = (store: string, frame: string) => JSON.stringify(['immediate', store, frame]);
const inScope = (snapshot: { scopeStoreGuid?: string }, storeGuid: string) =>
    snapshot.scopeStoreGuid === undefined || snapshot.scopeStoreGuid === storeGuid;
const contains = (setups: AutoCartSetupItem[], request: AutoCartSetupItemDraft, guid: string) =>
    setups.some(
        (setup) =>
            setup.storeGuid === request.storeGuid && setup.frameNumber === request.frameNumber && setup.scheduledCartSetupGuid === guid,
    );
function confirms(command: ScheduleCommand, snapshot: ScheduleObservation) {
    if (command.phase !== 'pending') return false;
    const { intent, expected } = command;
    const storeGuid = intent.kind === 'delete' ? intent.storeGuid : intent.request.storeGuid;
    if (!inScope(snapshot, storeGuid)) return false;
    if (intent.kind === 'delete') return !snapshot.setups.some((setup) => setup.scheduledCartSetupGuid === intent.guid);
    if (!expected) return false;
    if (intent.kind === 'immediate') return contains(snapshot.setups, intent.request, expected.scheduledCartSetupGuid);
    return snapshot.setups.some(
        (setup) =>
            setup.scheduledCartSetupGuid === expected.scheduledCartSetupGuid &&
            fields.every((field) => equals(setup[field] ?? null, expected[field] ?? null)),
    );
}
function updateConfirmationKey(setup: AutoCartSetupItem) {
    return JSON.stringify([
        setup.scheduledCartSetupGuid,
        ...fields.map((field) => {
            if (field !== 'cartSetupServiceParameters') return setup[field] ?? null;
            const parameters = setup.cartSetupServiceParameters;
            return parameters == null
                ? null
                : Object.entries(parameters)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([key, value]) => [key, typeof value, value]);
        }),
    ]);
}
function confirmationKey(intent: CommandIntent, expected?: AutoCartSetupItem) {
    if (intent.kind === 'delete') return 'absent';
    if (!expected) return undefined;
    return intent.kind === 'immediate' ? expected.scheduledCartSetupGuid : updateConfirmationKey(expected);
}
function collectEvidence(intent: CommandIntent, snapshot: ScheduleObservation, previous: Set<string>) {
    const storeGuid = intent.kind === 'delete' ? intent.storeGuid : intent.request.storeGuid;
    if (!inScope(snapshot, storeGuid)) return previous;
    const seen = new Set(previous);
    if (intent.kind === 'delete') {
        if (!snapshot.setups.some((setup) => setup.scheduledCartSetupGuid === intent.guid)) seen.add('absent');
    } else {
        for (const setup of snapshot.setups) {
            if (
                setup.storeGuid !== storeGuid ||
                (setup.frameNumber !== intent.request.frameNumber &&
                    !(intent.kind === 'update' && setup.scheduledCartSetupGuid === intent.baseline?.scheduledCartSetupGuid))
            )
                continue;
            seen.add(intent.kind === 'immediate' ? setup.scheduledCartSetupGuid : updateConfirmationKey(setup));
        }
    }
    return seen.size === previous.size ? previous : seen;
}
const commandTarget = (intent: CommandIntent) =>
    intent.kind === 'delete' ? { storeGuid: intent.storeGuid, frameNumber: intent.baseline?.frameNumber } : intent.request;

// Each principal owns its model. Editor interruptions retire a whole UI session, never replay requests.
export function createScheduleModel() {
    const state = createStore({ commands: new Map<string, ScheduleCommand>(), snapshots: new Map<string, ScheduleView>(), editorEpoch: 0 });
    const streams = new Map<string, { users: number; close: () => void; resync: () => void }>();
    let active = true;
    let initialSequence = 0;
    let editorOperations = 0;
    let editorRefreshRequested = false;
    const editorCurrent = (epoch: number) => active && state.get().editorEpoch === epoch;
    function interruptedEditor() {
        editorOperations = 0;
        editorRefreshRequested = false;
        return {
            ...state.get(),
            editorEpoch: state.get().editorEpoch + 1,
            commands: new Map([...state.get().commands].filter(([, command]) => command.intent.kind === 'immediate')),
        };
    }
    function publishSnapshot(scope: string, snapshot?: ScheduleView) {
        const previous = state.get().snapshots.get(scope);
        const interrupted =
            snapshot && (snapshot.isLoading || snapshot.error) && previous?.setups && !previous.isLoading && !previous.error;
        const next = interrupted ? interruptedEditor() : state.get();
        const snapshots = new Map(next.snapshots);
        if (snapshot) snapshots.set(scope, snapshot);
        else snapshots.delete(scope);
        state.set({ ...next, snapshots });
    }
    function refreshEditor() {
        if (!active) return;
        state.set({
            ...interruptedEditor(),
            snapshots: new Map([...state.get().snapshots].map(([scope]) => [scope, { isLoading: true, error: false }])),
        });
        for (const stream of streams.values()) stream.resync();
    }
    function flushEditorRefresh() {
        if (!editorRefreshRequested || editorOperations > 0) return;
        if ([...state.get().commands.values()].some((command) => command.intent.kind !== 'immediate' && command.phase === 'failed')) return;
        refreshEditor();
    }
    function put(id: string, command?: ScheduleCommand) {
        const commands = new Map(state.get().commands);
        if (command) commands.set(id, command);
        else commands.delete(id);
        state.set({ ...state.get(), commands });
    }
    function observe(snapshot: ScheduleObservation) {
        initialSequence = Math.max(initialSequence, snapshot.initialSnapshot);
        const commands = new Map(state.get().commands);
        for (const [id, command] of commands) {
            if (confirms(command, snapshot)) commands.delete(id);
            else if (command.phase === 'submitting') {
                const seen = collectEvidence(command.intent, snapshot, command.seen);
                if (seen !== command.seen) commands.set(id, { ...command, seen });
            }
        }
        const snapshots = new Map(state.get().snapshots);
        snapshots.set(scheduleScopeKey(snapshot.scopeStoreGuid), {
            setups: snapshot.setups,
            initialSnapshot: snapshot.initialSnapshot,
            isLoading: false,
            error: false,
        });
        state.set({ ...state.get(), commands, snapshots });
    }
    function forFrame(storeGuid: string, frameNumber: string) {
        return [...state.get().commands].find(([, command]) => {
            const target = commandTarget(command.intent);
            const source = command.intent.kind === 'update' ? command.intent.baseline : undefined;
            return (
                command.phase !== 'failed' &&
                ((target.storeGuid === storeGuid && sameScheduleFrame(target.frameNumber, frameNumber)) ||
                    (source?.storeGuid === storeGuid && sameScheduleFrame(source.frameNumber, frameNumber)))
            );
        });
    }
    async function execute(id: string, intent: CommandIntent, send: () => Promise<AutoCartSetupItem | undefined>): Promise<CommandResult> {
        if (!active) return 'inactive';
        const epoch = state.get().editorEpoch;
        const editor = intent.kind !== 'immediate';
        const previous = state.get().commands.get(id);
        const target = commandTarget(intent);
        if ((previous && previous.phase !== 'failed') || (target.frameNumber && forFrame(target.storeGuid, target.frameNumber)))
            return 'blocked';
        if (intent.kind === 'immediate') {
            const snapshot = state.get().snapshots.get(scheduleScopeKey(intent.scopeStoreGuid));
            if (
                !intent.request.frameNumber?.trim() ||
                !inScope(intent, intent.request.storeGuid) ||
                !snapshot?.setups ||
                snapshot.isLoading ||
                snapshot.error ||
                snapshot.setups.some(
                    (setup) =>
                        setup.storeGuid === intent.request.storeGuid && sameScheduleFrame(setup.frameNumber, intent.request.frameNumber),
                )
            )
                return 'unavailable';
        }
        put(id, { intent, phase: 'submitting', seen: new Set() });
        try {
            const expected = await send();
            if (editor && !editorCurrent(epoch)) return 'inactive';
            const current = state.get().commands.get(id);
            if (current?.phase !== 'submitting') return 'blocked';
            const key = confirmationKey(intent, expected);
            put(id, key !== undefined && current.seen.has(key) ? undefined : { intent, phase: 'pending', expected });
            return active ? 'accepted' : 'inactive';
        } catch (error) {
            if (editor && !editorCurrent(epoch)) return 'inactive';
            const current = state.get().commands.get(id);
            if (intent.kind === 'delete' && current?.phase === 'submitting' && current.seen.has('absent')) {
                put(id);
                return active ? 'accepted' : 'inactive';
            }
            if (editor && !isRejectedResponse(error, 'single')) {
                refreshEditor();
                return 'interrupted';
            }
            const failure = (error as { data?: { error?: unknown } } | undefined)?.data?.error ?? error;
            put(id, { intent, phase: 'failed', error: failure });
            throw error;
        }
    }
    async function executeBatch(batch: ScheduleBatch, send: () => Promise<void>): Promise<CommandResult> {
        if (!active) return 'inactive';
        const epoch = state.get().editorEpoch;
        if (!inScope(batch, batch.storeGuid) || batch.members.length < 1 || batch.members.length > 500) return 'blocked';
        const snapshot = state.get().snapshots.get(scheduleScopeKey(batch.scopeStoreGuid));
        if (!snapshot?.setups || snapshot.isLoading || snapshot.error) return 'unavailable';
        const ids = new Set<string>();
        const frames = new Set<string>();
        for (const { id, intent } of batch.members) {
            if (ids.has(id) || state.get().commands.has('editor:' + id)) return 'blocked';
            ids.add(id);
            const target = commandTarget(intent);
            if (target.storeGuid !== batch.storeGuid || intent.kind === 'immediate') return 'blocked';
            if (batch.kind === 'create') {
                const frame = target.frameNumber?.trim();
                if (intent.kind !== 'update' || !frame || frames.has(frame)) return 'blocked';
                frames.add(frame);
                if (
                    snapshot.setups.some(
                        (setup) =>
                            setup.storeGuid === target.storeGuid &&
                            (sameScheduleFrame(setup.frameNumber, target.frameNumber) || setup.scheduledCartSetupGuid === id),
                    )
                )
                    return 'blocked';
            } else if (intent.kind !== 'delete') return 'blocked';
            if (target.frameNumber && forFrame(target.storeGuid, target.frameNumber)) return 'blocked';
        }
        const commands = new Map(state.get().commands);
        for (const { id, intent } of batch.members) {
            const seen = collectEvidence(
                intent,
                {
                    setups: snapshot.setups,
                    scopeStoreGuid: batch.scopeStoreGuid,
                    initialSnapshot: snapshot.initialSnapshot ?? 0,
                },
                new Set(),
            );
            commands.set('editor:' + id, { intent, batch, phase: 'submitting', seen });
        }
        state.set({ ...state.get(), commands });
        try {
            await send();
            if (!editorCurrent(epoch)) return 'inactive';
            const commands = new Map(state.get().commands);
            for (const { id, intent, expected } of batch.members) {
                const key = 'editor:' + id;
                const command = commands.get(key);
                if (command?.phase !== 'submitting' || command.batch !== batch) continue;
                const confirmation = confirmationKey(intent, expected);
                if (confirmation !== undefined && command.seen.has(confirmation)) commands.delete(key);
                else commands.set(key, { intent, batch, phase: 'pending', expected });
            }
            state.set({ ...state.get(), commands });
            return 'accepted';
        } catch (error) {
            if (!editorCurrent(epoch)) return 'inactive';
            if (!isRejectedResponse(error, 'bulk')) {
                refreshEditor();
                return 'interrupted';
            }
            const commands = new Map(state.get().commands);
            for (const { id, intent } of batch.members) commands.set('editor:' + id, { intent, batch, phase: 'failed', error });
            state.set({ ...state.get(), commands });
            throw error;
        }
    }
    function canResolve(command: ScheduleCommand, snapshot: ScheduleSnapshot) {
        return (
            command.intent.kind === 'immediate' &&
            command.phase === 'pending' &&
            !!command.expected &&
            command.recoveryAfter !== undefined &&
            snapshot.ready &&
            snapshot.setups !== undefined &&
            inScope(snapshot, command.intent.request.storeGuid) &&
            snapshot.initialSnapshot !== undefined &&
            snapshot.initialSnapshot > command.recoveryAfter &&
            !contains(snapshot.setups, command.intent.request, command.expected.scheduledCartSetupGuid)
        );
    }
    return {
        state,
        observe,
        execute,
        executeBatch,
        canResolve,
        forFrame,
        editorCurrent,
        beginEditorOperation(epoch: number) {
            if (!editorCurrent(epoch)) return () => undefined;
            editorOperations++;
            let ended = false;
            return () => {
                if (ended || !editorCurrent(epoch)) return;
                ended = true;
                editorOperations--;
                flushEditorRefresh();
            };
        },
        finishEditor(epoch: number) {
            if (!editorCurrent(epoch)) return;
            editorRefreshRequested = true;
            flushEditorRefresh();
        },
        nextInitialSnapshot: () => ++initialSequence,
        publishSnapshot,
        setActive(value: boolean) {
            active = value;
            if (!value) state.set(interruptedEditor());
        },
        acquire(scope: string, connect: () => { close: () => void; resync: () => void }) {
            let stream = streams.get(scope);
            if (!stream) {
                stream = { users: 0, ...connect() };
                streams.set(scope, stream);
            }
            stream.users++;
            return () => {
                if (--stream.users === 0) {
                    stream.close();
                    streams.delete(scope);
                    publishSnapshot(scope);
                }
            };
        },
        resync: (scope: string) => streams.get(scope)?.resync(),
        acknowledge(id: string) {
            const command = state.get().commands.get(id);
            if (command?.phase === 'submitting') return;
            if (command?.batch) {
                const commands = new Map(state.get().commands);
                for (const [key, member] of commands) if (member.batch === command.batch) commands.delete(key);
                state.set({ ...state.get(), commands });
            } else put(id);
            flushEditorRefresh();
        },
        reload(id: string, snapshot: ScheduleSnapshot) {
            const command = state.get().commands.get(id);
            if (command?.phase !== 'pending') return;
            put(id, { ...command, recoveryAfter: Math.max(initialSequence, snapshot.initialSnapshot ?? 0) });
            snapshot.resync();
        },
        resolve(id: string, snapshot: ScheduleSnapshot) {
            const command = state.get().commands.get(id);
            if (command?.intent.kind === 'immediate' && (command.phase === 'failed' || canResolve(command, snapshot))) put(id);
        },
    };
}
export type ScheduleModel = ReturnType<typeof createScheduleModel>;
